import React, { useEffect, useRef, useState } from "react";
import { compressImage } from "../../../../../utils/compressImages";

export default function UploadProfile({ setProfile, url }) {
    const MAX_SIZE = 20 * 1024 * 1024; // 20 MB
    const ACCEPTED_TYPES = /^image\/(png|jpe?g|gif|webp|bmp|svg\+xml)$/i;
    const inputRef = useRef(null);

    const [file, setFile] = useState(null);
    const [previewSrc, setPreviewSrc] = useState(null);
    const [error, setError] = useState("");
    const [isDragging, setIsDragging] = useState(false);
    const [loading, setLoading] = useState(false);

    const normalizeIncomingUrl = (raw) => {
        if (!raw) return null;
        if (typeof raw !== "string") return null;
        const trimmed = raw.trim();
        if (trimmed.startsWith("data:")) return trimmed;
        if (/^https?:\/\//i.test(trimmed)) return trimmed;
        if (/^[A-Za-z0-9+/=\s]+$/.test(trimmed) && trimmed.length > 100) {
            return `data:image/png;base64,${trimmed.replace(/\s+/g, "")}`;
        }
        if (/^iVBOR/i.test(trimmed)) {
            return `data:image/png;base64,${trimmed}`;
        }
        return null;
    };

    // Reset input value so selecting the same file fires onChange
    const openFileDialog = () => {
        try {
            if (inputRef.current) inputRef.current.value = "";
        } catch (e) { }
        inputRef.current?.click();
    };

    const fileToDataUrl = (f) =>
        new Promise((resolve, reject) => {
            try {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = (e) => reject(e);
                reader.readAsDataURL(f);
            } catch (e) {
                reject(e);
            }
        });

    const validateImageSrc = (src) =>
        new Promise((resolve) => {
            if (!src) return resolve(false);
            const img = new Image();
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            img.src = src;
        });

    useEffect(() => {
        let mounted = true;
        (async () => {
            if (file) return;
            setError("");
            if (!url) {
                if (mounted) setPreviewSrc(null);
                return;
            }
            const normalized = normalizeIncomingUrl(url);
            if (!normalized) {
                if (mounted) setPreviewSrc(null);
                return;
            }
            const ok = await validateImageSrc(normalized);
            if (!mounted) return;
            if (ok) setPreviewSrc(normalized);
            else setPreviewSrc(null);
        })();
        return () => {
            mounted = false;
        };
    }, [url, file]);

    useEffect(() => {
        if (file) {
            try {
                setProfile?.(file);
            } catch (e) {
                console.warn("setProfile callback error:", e);
            }
        }
    }, [file, setProfile]);

    const handleRawFile = async (rawFile) => {
        setError("");
        if (!rawFile) return;
        if (!rawFile.type || !ACCEPTED_TYPES.test(rawFile.type)) {
            setError("Please upload a valid image (PNG, JPG, JPEG, GIF, WEBP, BMP, or SVG).");
            return;
        }
        if (rawFile.size > MAX_SIZE) {
            setError("File is too large. Maximum allowed size is 20MB.");
            return;
        }

        try {
            setLoading(true);
            let compressed = await compressImage(rawFile);
            if (!compressed) compressed = rawFile;
            const dataUrl = await fileToDataUrl(compressed);
            const ok = await validateImageSrc(dataUrl);
            if (!ok) {
                setError("Uploaded image could not be loaded. Try another file.");
                setLoading(false);
                return;
            }
            setFile(compressed);
            setPreviewSrc(dataUrl);
            setLoading(false);
        } catch (err) {
            console.error("Error processing file:", err);
            setError("Error while processing image.");
            setLoading(false);
        }
    };

    const onInputChange = async (e) => {
        const f = e.target.files?.[0];
        await handleRawFile(f);
    };

    const onDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        setIsDragging(true);
    };
    const onDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };
    const onDrop = async (e) => {
        e.preventDefault();
        setIsDragging(false);
        const f = e.dataTransfer.files?.[0];
        await handleRawFile(f);
    };

    const clearSelection = async () => {
        setError("");
        setFile(null);
        const normalized = normalizeIncomingUrl(url);
        if (normalized) {
            const ok = await validateImageSrc(normalized);
            if (ok) {
                setPreviewSrc(normalized);
                try {
                    setProfile?.(normalized);
                } catch (e) {
                    console.warn("setProfile callback error:", e);
                }
                return;
            }
        }
        setPreviewSrc(null);
        try {
            setProfile?.(null);
        } catch (e) {
            console.warn("setProfile callback error:", e);
        }
    };

    const placeholder = (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-20 h-20 text-white opacity-90" aria-hidden>
            <path fill="currentColor" d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10zm0 2c-4.97 0-9 2.5-9 5v1h18v-1c0-2.5-4.03-5-9-5z" />
        </svg>
    );

    return (
        <div className="bg-white rounded-xl p-2 md:p-4 shadow">
            <h2 className="text-center text-lg sm:text-xl font-medium my-2">Upload your profile picture</h2>

            <div
                role="button"
                tabIndex={0}
                onClick={openFileDialog}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") openFileDialog();
                }}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragEnter={onDragOver}
                onDragLeave={onDragLeave}
                className={`flex flex-col sm:flex-row items-center gap-6 rounded-lg p-2 cursor-pointer ${isDragging ? "border-2 border-dashed border-indigo-300" : ""}`}
            >
                <div className="flex-shrink-0">
                    <div className="w-36 h-36 sm:w-28 sm:h-28 md:w-36 md:h-36 rounded-full flex items-center justify-center bg-indigo-600 text-white overflow-hidden">
                        {previewSrc ? (
                            <img src={previewSrc} alt="profile preview" className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                            placeholder
                        )}
                    </div>
                </div>

                <div className="flex-1 text-center sm:text-left">
                    <p className="text-sm text-gray-600 dark:text-slate-300">Supported formats: JPG, PNG, JPEG</p>
                    <p className="text-sm text-gray-600 dark:text-slate-300">Max size: 20MB</p>

                    <div className="my-2">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                openFileDialog();
                            }}
                            className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                        >
                            Click here to upload
                        </button>
                    </div>

                    <p className="my-2 text-xs text-gray-500 dark:text-slate-400">Tip: Use a clear, front-facing photo for better visibility to employers.</p>

                    <div className="my-2 flex items-center justify-center sm:justify-start gap-3">
                        {file && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    clearSelection();
                                }}
                                className="text-sm px-3 py-1.5 bg-red-50 text-red-700 border border-red-100 rounded-md hover:bg-red-100"
                            >
                                Remove
                            </button>
                        )}
                        {loading && <span className="text-sm text-gray-500">Processing image...</span>}
                    </div>

                    {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
                </div>

                <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onInputChange} aria-hidden />
            </div>
        </div>
    );
}
